import React from "react";
import { Link } from "react-router-dom";
import { SignOut, User, CaretDown } from "@phosphor-icons/react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useAuth, ROLE_LABELS } from "../../contexts/AuthContext";
import { cn } from "../../lib/utils";

// Iter 102.3 · Phase B — Sidebar Profile Menu relocation.
// Per EDDR-002, Profile / Role / Sign Out move from the sidebar footer
// to the top-right UserMenu in AppLayout's header. Sidebar height is
// reclaimed for business navigation only.
//
// The menu carries operator-scoped identity + session controls only —
// Profile / (future) Preferences / (future) Settings / (future) Console
// switching / Sign Out. Business navigation (ESS, HR screens, etc.)
// stays in the sidebar to preserve a single mental model per surface.
//
// All data-testids from the previous sidebar footer are RELOCATED (not
// renamed) here so existing E2E selectors continue to work:
//   • sidebar-user-profile-link  → still on the /profile <Link>
//   • sidebar-user-name          → still on the name text
//   • sidebar-user-role          → still on the role badge
//   • logout-btn                 → still on the Sign Out item
export const UserMenu = () => {
    const { user, logout } = useAuth();
    if (!user) return null;

    const initial = (user.name || user.email || "?").charAt(0).toUpperCase();
    const roleLabel = ROLE_LABELS[user.role] || user.role;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    data-testid="user-menu-trigger"
                    aria-label="Account menu"
                    className={cn(
                        "inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border border-slate-200",
                        "hover:bg-slate-50 hover:border-slate-300 transition-colors",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200",
                    )}
                >
                    <span
                        className="h-7 w-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-[11px] font-bold"
                        aria-hidden="true"
                    >
                        {initial}
                    </span>
                    <span className="hidden sm:flex flex-col items-start leading-tight">
                        <span
                            className="text-[12px] font-semibold text-slate-900 max-w-[140px] truncate"
                            data-testid="user-menu-name"
                        >
                            {user.name}
                        </span>
                        <span
                            className="text-[10px] uppercase tracking-[0.14em] text-slate-500 max-w-[140px] truncate"
                            data-testid="user-menu-role"
                        >
                            {roleLabel}
                        </span>
                    </span>
                    <CaretDown size={12} weight="bold" className="text-slate-400 ml-0.5" />
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="end"
                sideOffset={8}
                className="w-64"
                data-testid="user-menu-content"
            >
                <div className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                        <span
                            className="h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold shrink-0"
                            aria-hidden="true"
                        >
                            {initial}
                        </span>
                        <div className="min-w-0">
                            <div
                                className="text-sm font-semibold text-slate-900 truncate"
                                data-testid="sidebar-user-name"
                            >
                                {user.name}
                            </div>
                            <div className="text-xs text-slate-500 truncate" title={user.email}>
                                {user.email}
                            </div>
                        </div>
                    </div>
                    <div
                        className="reedos-eyebrow mt-2 text-[10px] tracking-[0.16em]"
                        data-testid="sidebar-user-role"
                    >
                        {roleLabel}
                    </div>
                </div>

                <DropdownMenuSeparator />

                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.16em] text-slate-400 px-3">
                    Account
                </DropdownMenuLabel>

                <DropdownMenuItem asChild>
                    <Link
                        to="/profile"
                        data-testid="sidebar-user-profile-link"
                        className="flex items-center gap-2.5 px-3 py-2 cursor-pointer"
                    >
                        <User size={16} className="text-slate-500" />
                        <span className="text-sm">Account Settings</span>
                    </Link>
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                    onSelect={(e) => {
                        e.preventDefault();
                        logout();
                    }}
                    data-testid="logout-btn"
                    className="flex items-center gap-2.5 px-3 py-2 text-rose-700 focus:text-rose-800 focus:bg-rose-50 cursor-pointer"
                >
                    <SignOut size={16} weight="bold" />
                    <span className="text-sm font-semibold">Sign Out</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default UserMenu;
